import {BehaviorSubject, firstValueFrom, from} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MmProjectLoaderService} from '../../../../mm-project/mm-project-loader.service';
import {EventEmitter, Injectable} from '@angular/core';
import {getCompanyInfo} from '@docma-mm-core/company/company';
import {TranslateService} from '@ngx-translate/core';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {SelectItem} from '@mydocma/dynamic-form';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {MmViewService} from '../../../../mm-view.service';
import {getOrderInfo} from '@docma-mm-core/order/order';
import {LastUsedEntitiesService} from '@docma-mm-core/personalization/last-used-entities.service';


@Injectable()
@UntilDestroy()
export class MmCompanyDependencyService {



  selectedCompanySubject = new BehaviorSubject<{value: SelectItem, sender: NotificationSenderEnum}>(null);
  selectedCompany$ = this.selectedCompanySubject.asObservable();

  selectedCraftSubject = new BehaviorSubject<{value: SelectItem, sender: NotificationSenderEnum}>(null);
  selectedCraft$ = this.selectedCraftSubject.asObservable();

  selectedOrderSubject = new BehaviorSubject<{value: SelectItem, sender: NotificationSenderEnum}>(null);
  selectedOrder$ = this.selectedOrderSubject.asObservable();

  allCompanies = this.projectLoaderService.companies
    .pipe(
      filter(m => m?.loadState.status === LoadStatusEnum.Loaded),
      map(m => m.data.filter(c => c.IsActive === true && c.IsDeleted === false && (c?.IsInsolvent ?? false) === false)
        .map(c => ({id: c.Id, text: (c.ShortName + (c.Name1 ? ' - ' + c.Name1 : '')) + getCompanyInfo(c, this.translateService), data: c}))
      ),
      untilDestroyed(this));

  allCrafts = this.projectLoaderService.crafts
    .pipe(
      filter(m => m?.loadState.status === LoadStatusEnum.Loaded),
      map(m => m.data
        .map(c => ({id: c.Id, text: `${c.Name1} ${(c.Name2 ? ' - ' + c.Name2 : '')}`, data: c}))
      ),
      untilDestroyed(this));

  allOrders = this.projectLoaderService.orders
    .pipe(
      filter(m => m?.loadState.status === LoadStatusEnum.Loaded),
      map(m => m.data.filter(c => c.IsActive === true && c.IsDeleted === false
                                                                  && c.Company.IsActive === true && c.Company.IsDeleted === false && (c.Company?.IsInsolvent ?? false) === false)
        .map(o => ({id: o.Id, text: `${o.Description1} ${(o.Description2 ? ' - ' + o.Description2 : '')}` + getOrderInfo(o, o.Company, this.translateService), data: o}))
      ),
      untilDestroyed(this));

  companyInfoMessageSubject = new BehaviorSubject<string>(null);
  companyInfoMessage$ = this.companyInfoMessageSubject.asObservable();

  craftInfoMessageSubject = new BehaviorSubject<string>(null);
  craftInfoMessage$ = this.craftInfoMessageSubject.asObservable();

  orderInfoMessageSubject = new BehaviorSubject<string>(null);
  orderInfoMessage$ = this.orderInfoMessageSubject.asObservable();

  recentlyUsedCompanies: { [index: number]: number };
  recentlyUsedCrafts: { [index: number]: number };
  recentlyUsedOrders: { [index: number]: number };

  onFieldValueChanged = new EventEmitter<any>();

  constructor(private readonly projectLoaderService: MmProjectLoaderService,
              private readonly personalizationService: PersonalizationService,
              private readonly mmViewService: MmViewService,
              private readonly translateService: TranslateService,
              private readonly lastUsedEntitiesService: LastUsedEntitiesService) {
  }

  async getDeactivatedCompanies(companyId: number) {
    return await firstValueFrom(this.projectLoaderService.companies
      .pipe(
        filter(m => m?.loadState.status === LoadStatusEnum.Loaded),
        map(m => m.data.filter(c =>
            (c.IsActive === false || c.IsDeleted === true || (c?.IsInsolvent ?? false) === true) && c.Id === companyId)
          .map(c => ({id: c.Id, text: (c.ShortName + (c.Name1 ? ' - ' + c.Name1 : '')) + getCompanyInfo(c, this.translateService), data: c}))
        ),
        untilDestroyed(this)));

  }

  async getDeactivatedOrders(orderId: number, companyId: number) {
    return await firstValueFrom(this.projectLoaderService.orders
      .pipe(
        filter(m => m?.loadState.status === LoadStatusEnum.Loaded),
        map(m => m.data.filter(o =>
            (o.IsActive === false || o.IsDeleted === true || o.Company.IsActive === false || o.Company.IsDeleted === true || (o.Company?.IsInsolvent ?? false) === true) && o.Id === orderId )
          .map(o => ({id: o.Id, text: `${o.Description1} ${(o.Description2 ? ' - ' + o.Description2 : '')}` + getOrderInfo(o, o.Company, this.translateService), data: o}))
        ),
        untilDestroyed(this)));
  }

  addRecentlyUsedEntities(allEntities: SelectItem[], recentlyUsedEntities: { [index: number]: number }, groupItems: {[id: number]: SelectItem}) {
    const items: SelectItem[] = [];

    if (recentlyUsedEntities) {
      Object.keys(recentlyUsedEntities).map(r => {
        const found = allEntities.find(f => f.id === recentlyUsedEntities[r])
        if (found) {
          items.push(found);
        }
      });
    }

    if ((items?.length ?? 0) > 0) {
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyLastUsed');
      groupItems[1] = {id: 1, text: nameFunc(), data: nameFunc};
      groupItems[1].items = items;
    }
  }

  async loadRecentlyUsedEntities(personalizationKey: string) {
    const settingsObj = await this.lastUsedEntitiesService.loadLastUsedEntities(personalizationKey, undefined, true);
    if (personalizationKey === PersonalizationKeysEnum.RecentlyUsedCompanies) {
      this.recentlyUsedCompanies = settingsObj;
    }
    else if (personalizationKey === PersonalizationKeysEnum.RecentlyUsedCrafts) {
      this.recentlyUsedCrafts = settingsObj;
    }
    else if (personalizationKey === PersonalizationKeysEnum.RecentlyUsedOrders) {
      this.recentlyUsedOrders = settingsObj;
    }
  }

  async saveRecentlyUsedEntities(entityId: number, personalizationKey: string) {
    const newSettingsObj = await this.lastUsedEntitiesService.saveLastUsedEntities(entityId, personalizationKey, 5, true)

    if (personalizationKey === PersonalizationKeysEnum.RecentlyUsedCompanies) {
      this.recentlyUsedCompanies = newSettingsObj;
    } else if (personalizationKey === PersonalizationKeysEnum.RecentlyUsedCrafts) {
      this.recentlyUsedCrafts = newSettingsObj;
    } else if (personalizationKey === PersonalizationKeysEnum.RecentlyUsedOrders) {
      this.recentlyUsedOrders = newSettingsObj;
    }
  }


}

export enum NotificationSenderEnum {
  initialize = 0,
  company = 1,
  craft = 2,
  order = 3
}
